import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { stoppedBy, textIn, type Answers, type Lang, type StopHit } from '@rx-manager/forms';
import { firstUnanswered, FormRunner, type DraftAnswers } from '@rx-manager/forms/react';
import {
  FormApiError,
  formsApi,
  type FormGate,
  type FormPharmacy,
  type FormReady,
  type FormSent,
} from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { Alert, Button, HEADING, Spinner, cn } from '../../components/ui';
import { DateOfBirth, EMPTY_DOB, isoFromParts, type DobParts } from './DateOfBirth';
import { FormStateMessage, type ClosedState } from './FormStates';
import { forgetSession, newNonce, rememberSession, storedSession } from './form-session';
import { TimeoutDialog, usePrivacyTimeout } from './PrivacyTimeout';

type View =
  | { kind: 'loading' }
  | { kind: 'dob' }
  | { kind: 'form' }
  /** `stop`: an answer the pharmacy said to call about (SFM-D36), shown after sending. */
  | { kind: 'sent'; next: FormSent['next']; stop: StopHit | null }
  | { kind: 'closed'; why: 'timeout' | 'other_device' }
  | { kind: 'state'; state: ClosedState }
  | { kind: 'problem'; key: MessageKey };

const LINK_STATES: ReadonlySet<string> = new Set([
  'submitted',
  'expired',
  'locked',
  'cancelled',
  'not_needed',
  'replaced',
  'used',
  'restart',
  'unavailable',
]);

/** Today on this device's calendar: date questions are checked again by the server. */
function today(): string {
  return new Date().toLocaleDateString('en-CA');
}

/**
 * A form link: `/f/<token>` (SFM-D8 to D11).
 *
 * A personal link asks for the date of birth first, and shows nothing of the
 * form until it matches. A counter code opens straight away. A session made on
 * this device (the booking's done screen, a reload, the visit's next form) is
 * resumed without asking again. Each Next saves a draft on the server; the
 * form closes itself after twenty quiet minutes, with a warning two before.
 */
export function FormPage({
  token,
  onPharmacy,
  onLanguages,
}: {
  token: string;
  onPharmacy: (pharmacy: FormPharmacy) => void;
  onLanguages: (languages: Lang[]) => void;
}) {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [pharmacy, setPharmacy] = useState<FormPharmacy | null>(null);
  const [ready, setReady] = useState<FormReady | null>(null);
  const [answers, setAnswers] = useState<DraftAnswers>({});
  const [limits, setLimits] = useState<{ idleUntil: string; until: string | null } | null>(null);
  const [dob, setDob] = useState<DobParts>(EMPTY_DOB);
  const [dobError, setDobError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<'updated' | 'save_failed' | null>(null);
  const [extending, setExtending] = useState(false);
  const nonce = useRef(newNonce());
  const heading = useRef<HTMLHeadingElement>(null);

  const lang: Lang = ready && ready.languages.includes(locale) ? locale : 'en';
  const secret = (): string => storedSession(token) ?? ready?.session.secret ?? '';

  const show = useCallback(
    (result: FormReady | FormGate) => {
      setPharmacy(result.pharmacy);
      onPharmacy(result.pharmacy);
      if (result.state === 'ready') {
        rememberSession(token, result.session.secret);
        setReady(result);
        setAnswers(result.draft ?? {});
        setLimits({ idleUntil: result.session.idleUntil, until: result.session.until });
        onLanguages(result.languages);
        // The language they answered in last time, if the form has it.
        if (result.language && result.languages.includes(result.language)) {
          setLocale(result.language);
        }
        setView({ kind: 'form' });
        return;
      }
      if (result.state === 'verify_dob') {
        setView({ kind: 'dob' });
        return;
      }
      forgetSession(token);
      setView({ kind: 'state', state: result.state });
    },
    [token, onPharmacy, onLanguages, setLocale],
  );

  const failed = useCallback((error: unknown) => {
    if (error instanceof FormApiError && error.status === 404) {
      setView({ kind: 'state', state: 'missing' });
    } else if (error instanceof FormApiError && error.status === 0) {
      setView({ kind: 'problem', key: 'error.offline' });
    } else {
      setView({ kind: 'problem', key: 'error.unknown' });
    }
  }, []);

  const load = useCallback(async () => {
    setView({ kind: 'loading' });
    const stored = storedSession(token);
    try {
      if (stored) {
        try {
          show(await formsApi.resume(token, stored));
          return;
        } catch (error) {
          // An ended or replaced session: start again from the link.
          if (!(error instanceof FormApiError) || error.status !== 401) throw error;
          forgetSession(token);
        }
      }
      show(await formsApi.open(token));
    } catch (error) {
      failed(error);
    }
  }, [token, show, failed]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (view.kind === 'dob' || view.kind === 'closed' || view.kind === 'sent') {
      heading.current?.focus();
    }
  }, [view.kind]);

  /** A refusal that means the form is no longer this page's to fill in. True when handled. */
  const handled = (error: unknown): boolean => {
    if (!(error instanceof FormApiError)) return false;
    if (error.status === 401) {
      forgetSession(token);
      setLimits(null);
      setView({ kind: 'closed', why: error.code === 'session_replaced' ? 'other_device' : 'timeout' });
      return true;
    }
    if (error.code === 'version_changed') {
      setNotice('updated');
      const stored = storedSession(token);
      if (stored) void formsApi.resume(token, stored).then(show, failed);
      return true;
    }
    if (error.status === 409 && LINK_STATES.has(error.code)) {
      forgetSession(token);
      setLimits(null);
      setView({ kind: 'state', state: error.code as ClosedState });
      return true;
    }
    return false;
  };

  const verify = async () => {
    const iso = isoFromParts(dob);
    if (!iso) {
      setDobError(t('form.dobInvalid'));
      return;
    }
    setBusy(true);
    setDobError(null);
    try {
      show(await formsApi.verify(token, iso));
    } catch (error) {
      if (error instanceof FormApiError && error.code === 'dob_mismatch') {
        const left = Number(error.extra.triesLeft ?? 0);
        setDobError(
          `${t('form.dobMismatch')} ${left === 1 ? t('form.triesLeftOne') : t('form.triesLeft', { count: left })}`,
        );
      } else if (error instanceof FormApiError && error.code === 'locked') {
        setView({ kind: 'state', state: 'locked' });
      } else if (error instanceof FormApiError && error.code === 'dob_invalid') {
        setDobError(t('form.dobInvalid'));
      } else {
        failed(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const save = (current: DraftAnswers) => {
    if (!ready) return;
    void formsApi
      .saveDraft({ token, session: secret(), version: ready.version, answers: current, language: lang })
      .then((saved) => {
        setLimits((previous) => ({ idleUntil: saved.idleUntil, until: previous?.until ?? null }));
        setNotice((previous) => (previous === 'save_failed' ? null : previous));
      })
      .catch((error: unknown) => {
        if (!handled(error)) setNotice('save_failed');
      });
  };

  const submit = async (checked: Answers) => {
    if (!ready) return;
    try {
      const sent = await formsApi.submit({
        token,
        session: secret(),
        version: ready.version,
        answers: checked,
        language: lang,
        nonce: nonce.current,
      });
      forgetSession(token);
      setLimits(null);
      // The same rule the server keeps for staff: the pharmacy's words, now the form is sent.
      setView({ kind: 'sent', next: sent.next, stop: stoppedBy(ready.form, checked) });
    } catch (error) {
      if (handled(error)) return;
      throw error;
    }
  };

  const close = async (why: 'timeout') => {
    const current = secret();
    forgetSession(token);
    setLimits(null);
    setView({ kind: 'closed', why });
    if (current) await formsApi.end(token, current).catch(() => undefined);
  };

  const { warning } = usePrivacyTimeout(view.kind === 'form' ? limits : null, () => void close('timeout'));

  const keepGoing = async () => {
    setExtending(true);
    try {
      const extended = await formsApi.extend(token, secret());
      setLimits((previous) => ({ idleUntil: extended.idleUntil, until: previous?.until ?? null }));
    } catch (error) {
      if (!handled(error)) void close('timeout');
    } finally {
      setExtending(false);
    }
  };

  if (view.kind === 'loading') return <Spinner label={t('form.loading')} />;

  if (view.kind === 'problem') {
    return (
      <div className="space-y-4">
        <Alert>{t(view.key)}</Alert>
        <Button onClick={() => void load()}>{t('error.retry')}</Button>
      </div>
    );
  }

  if (view.kind === 'state') return <FormStateMessage state={view.state} pharmacy={pharmacy} />;

  if (view.kind === 'dob') {
    return (
      <form
        noValidate
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          void verify();
        }}
      >
        <div className="space-y-2">
          <h1 ref={heading} tabIndex={-1} className={HEADING}>
            {t('form.dobHeading')}
          </h1>
          <p className="text-lg text-foreground">{t('form.dobIntro')}</p>
        </div>
        <DateOfBirth
          value={dob}
          onChange={(next) => {
            setDob(next);
            setDobError(null);
          }}
          error={dobError}
          legend={t('form.dobLegend')}
          hint={t('form.dobHint')}
          announce
        />
        <Button type="submit" loading={busy}>
          {t('form.continue')}
        </Button>
      </form>
    );
  }

  if (view.kind === 'closed') {
    return (
      <div className="space-y-4">
        <h1 ref={heading} tabIndex={-1} className={HEADING}>
          {t('form.closedHeading')}
        </h1>
        <p className="text-lg text-foreground">
          {view.why === 'other_device' ? t('form.otherDevice') : t('form.closedBody')}
        </p>
        <Button onClick={() => void load()}>{t('form.openAgain')}</Button>
      </div>
    );
  }

  if (view.kind === 'sent') {
    return (
      <div className="space-y-5">
        <h1
          ref={heading}
          tabIndex={-1}
          className={cn('flex items-start gap-3', HEADING)}
        >
          <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-success" aria-hidden="true" />
          {t('form.sentHeading')}
        </h1>
        <p className="text-lg text-foreground">
          {t('form.sentBody', { pharmacy: pharmacy?.displayName ?? '' })}
        </p>
        {view.stop && (
          <section
            aria-labelledby="form-stop-heading"
            className="space-y-2 rounded-xl border border-danger/30 bg-danger/5 p-5"
          >
            <h2 id="form-stop-heading" className="text-lg font-semibold text-heading">
              {t('form.stopAfterHeading')}
            </h2>
            <p lang={lang} className="whitespace-pre-line text-lg text-foreground">
              {textIn(view.stop.message, lang)}
            </p>
            {pharmacy?.supportPhone && (
              <p className="text-base text-foreground">
                {t('form.stopAfterCall', { phone: pharmacy.supportPhone })}
              </p>
            )}
          </section>
        )}
        {view.next && (
          <div className="space-y-3 rounded-xl border border-primary/25 bg-accent/50 p-5">
            <p className="text-base text-foreground">{t('form.nextFormIntro')}</p>
            <Button
              onClick={() => {
                rememberSession(view.next!.token, view.next!.session);
                navigate(`/f/${view.next!.token}`);
              }}
            >
              {t('form.nextForm')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notice && (
        <p role="status" className="rounded-xl border border-warning/50 bg-warning/15 p-4 text-base text-foreground">
          {notice === 'updated' ? t('form.updated') : t('form.saveFailed')}
        </p>
      )}
      {ready && (
        <FormRunner
          key={ready.version}
          schema={ready.form}
          lang={lang}
          answers={answers}
          onAnswersChange={setAnswers}
          onStep={save}
          onSubmit={submit}
          today={today()}
          startAt={Object.keys(ready.draft ?? {}).length > 0 ? firstUnanswered(ready.form, ready.draft) : undefined}
        />
      )}
      {warning && (
        <TimeoutDialog busy={extending} onKeepGoing={() => void keepGoing()} onClose={() => void close('timeout')} />
      )}
    </div>
  );
}
